import { AlertCircle, MessageCircle } from 'lucide-react';
import { useMemo, useState } from 'react';
import { cn } from '@/lib/utils';
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/components/ui';
import { WhatsAppMessagePreview } from '@/components/whatsapp/WhatsAppMessagePreview';
import {
	PATIENT_EDUCATION_LEVEL_OPTIONS,
	PATIENT_GENDER_OPTIONS,
	PATIENT_MARITAL_STATUS_OPTIONS,
} from '@/constants/patient';
import { maskCPF, maskPhone, removeMask } from '@/utils/masks';
import {
	buildDefaultSessionWhatsAppMessage,
	isValidWhatsAppPhone,
} from '@/utils/sessionWhatsAppDisplay';
import {
	PATIENT_PHONE_INPUT_ID,
	PATIENT_WHATSAPP_SECTION_ID,
} from '@/utils/patientWhatsAppNotifications';
import type { PatientFormData } from '../utils/patientSchedulingForm';

type PatientSchedulingFormFieldsProps = {
	formData: PatientFormData;
	errors: Record<string, string>;
	onChange: (next: PatientFormData) => void;
	canEditWhatsAppNotifications?: boolean;
	whatsappExampleProfessionalName?: string;
	whatsappExampleSessionDateIso?: string;
};

const labelClass =
	'ml-1 text-[10px] font-black uppercase tracking-widest text-stone-300';
const sectionClass =
	'text-[11px] font-black uppercase tracking-[0.2em] text-stone-400';
const inputBaseClass =
	'w-full bg-stone-50 border rounded-xl px-4 py-3 text-sm font-bold text-stone-600 outline-none transition-all box-border disabled:opacity-50';

const PatientSchedulingFormFields = ({
	formData,
	errors,
	onChange,
	canEditWhatsAppNotifications = true,
	whatsappExampleProfessionalName,
	whatsappExampleSessionDateIso,
}: PatientSchedulingFormFieldsProps) => {
	const [isExamplePreviewOpen, setIsExamplePreviewOpen] = useState(false);

	const exampleMessage = useMemo(
		() =>
			buildDefaultSessionWhatsAppMessage('SESSION_SCHEDULED', {
				patientName: formData.full_name.trim() || 'Paciente',
				sessionDateIso: whatsappExampleSessionDateIso,
				professionalName: whatsappExampleProfessionalName,
			}),
		[
			formData.full_name,
			whatsappExampleProfessionalName,
			whatsappExampleSessionDateIso,
		],
	);
	const getFieldClass = (fieldName: string) =>
		cn(
			inputBaseClass,
			errors[fieldName]
				? 'border-rose-500 bg-rose-50/30 focus:border-rose-600'
				: 'border-stone-200 focus:border-stone-400 focus:bg-white',
		);

	const ErrorMsg = ({ field }: { field: string }) =>
		errors[field] ? (
			<span className="animate-in fade-in slide-in-from-top-1 ml-1 mt-1 flex items-center gap-1 text-[10px] font-bold text-rose-500">
				<AlertCircle size={10} /> {errors[field]}
			</span>
		) : null;

	const renderOptionalSelect = <T extends string>({
		label,
		field,
		value,
		options,
		onValueChange,
	}: {
		label: string;
		field: string;
		value: T | '';
		options: Array<[T, string]>;
		onValueChange: (value: T) => void;
	}) => (
		<div>
			<label className={labelClass}>{label}</label>
			<Select
				value={value}
				onValueChange={(nextValue) => onValueChange(nextValue as T)}
			>
				<SelectTrigger
					className={cn(
						getFieldClass(field),
						'h-auto min-h-[46px] font-bold',
						!value && 'text-stone-400',
					)}
				>
					<SelectValue>
						{value
							? options.find(
									([optionValue]) => optionValue === value,
							  )?.[1]
							: 'Selecione'}
					</SelectValue>
				</SelectTrigger>
				<SelectContent>
					{options.map(([optionValue, optionLabel]) => (
						<SelectItem key={optionValue} value={optionValue}>
							{optionLabel}
						</SelectItem>
					))}
				</SelectContent>
			</Select>
			<ErrorMsg field={field} />
		</div>
	);

	return (
		<div className="space-y-8">
			<section className="space-y-4">
				<h3 className={sectionClass}>Identificação</h3>
				<div>
					<label className={labelClass}>Nome completo</label>
					<input
						type="text"
						value={formData.full_name}
						onChange={(event) =>
							onChange({
								...formData,
								full_name: event.target.value,
							})
						}
						className={getFieldClass('full_name')}
						placeholder="Ex: Maria Silva Santos"
						autoComplete="name"
					/>
					<ErrorMsg field="full_name" />
				</div>
				<div className="grid grid-cols-1 gap-4 md:grid-cols-2">
					<div>
						<label className={labelClass}>CPF (opcional)</label>
						<input
							type="text"
							value={formData.cpf}
							onChange={(event) =>
								onChange({
									...formData,
									cpf: maskCPF(event.target.value),
								})
							}
							className={getFieldClass('cpf')}
							placeholder="000.000.000-00"
							inputMode="numeric"
							maxLength={14}
						/>
						<ErrorMsg field="cpf" />
					</div>
					<div>
						<label className={labelClass}>RG (opcional)</label>
						<input
							type="text"
							value={formData.rg}
							onChange={(event) =>
								onChange({
									...formData,
									rg: event.target.value,
								})
							}
							className={getFieldClass('rg')}
							maxLength={20}
						/>
						<ErrorMsg field="rg" />
					</div>
				</div>
				<div className="grid grid-cols-1 gap-4 md:grid-cols-2">
					<div>
						<label className={labelClass}>
							Data de nascimento (opcional)
						</label>
						<input
							type="date"
							value={formData.birth_date}
							onChange={(event) =>
								onChange({
									...formData,
									birth_date: event.target.value,
								})
							}
							className={getFieldClass('birth_date')}
						/>
						<ErrorMsg field="birth_date" />
					</div>
					{renderOptionalSelect({
						label: 'Gênero (opcional)',
						field: 'gender',
						value: formData.gender,
						options: PATIENT_GENDER_OPTIONS,
						onValueChange: (value) =>
							onChange({ ...formData, gender: value }),
					})}
				</div>
			</section>

			<section className="space-y-4">
				<h3 className={sectionClass}>Dados sociodemográficos</h3>
				<div className="grid grid-cols-1 gap-4 md:grid-cols-2">
					{renderOptionalSelect({
						label: 'Estado civil (opcional)',
						field: 'marital_status',
						value: formData.marital_status,
						options: PATIENT_MARITAL_STATUS_OPTIONS,
						onValueChange: (value) =>
							onChange({ ...formData, marital_status: value }),
					})}
					{renderOptionalSelect({
						label: 'Escolaridade (opcional)',
						field: 'education_level',
						value: formData.education_level,
						options: PATIENT_EDUCATION_LEVEL_OPTIONS,
						onValueChange: (value) =>
							onChange({ ...formData, education_level: value }),
					})}
				</div>
				<div>
					<label className={labelClass}>Profissão (opcional)</label>
					<input
						type="text"
						value={formData.profession}
						onChange={(event) =>
							onChange({
								...formData,
								profession: event.target.value,
							})
						}
						className={getFieldClass('profession')}
						maxLength={255}
					/>
					<ErrorMsg field="profession" />
				</div>
			</section>

			<section className="space-y-4">
				<h3 className={sectionClass}>Contato</h3>
				<div className="grid grid-cols-1 gap-4 md:grid-cols-2">
					<div>
						<label className={labelClass}>
							Telefone (opcional)
						</label>
						<input
							id={PATIENT_PHONE_INPUT_ID}
							type="text"
							value={formData.phone}
							onChange={(event) =>
								onChange({
									...formData,
									phone: maskPhone(event.target.value),
								})
							}
							className={getFieldClass('phone')}
							placeholder="(11) 98765-4321"
							inputMode="numeric"
							maxLength={15}
						/>
						<ErrorMsg field="phone" />
					</div>
					<div>
						<label className={labelClass}>E-mail (opcional)</label>
						<input
							type="email"
							value={formData.email}
							onChange={(event) =>
								onChange({
									...formData,
									email: event.target.value,
								})
							}
							className={getFieldClass('email')}
							autoComplete="email"
						/>
						<ErrorMsg field="email" />
					</div>
				</div>
				<div>
					<label className={labelClass}>
						Contato de emergência (opcional)
					</label>
					<input
						type="text"
						value={formData.emergency_contact}
						onChange={(event) =>
							onChange({
								...formData,
								emergency_contact: event.target.value,
							})
						}
						className={getFieldClass('emergency_contact')}
						maxLength={255}
						placeholder="Ex: 11999998888 — tia Rosa"
					/>
					<ErrorMsg field="emergency_contact" />
				</div>
				<div
					id={PATIENT_WHATSAPP_SECTION_ID}
					className={cn(
						'flex items-center justify-between gap-4 rounded-xl border border-stone-200 bg-stone-50/80 px-4 py-3',
						!canEditWhatsAppNotifications && 'opacity-80',
					)}
				>
					<div className="min-w-0">
						<p className="flex items-center gap-2 text-sm font-semibold text-stone-800">
							<MessageCircle
								className={cn(
									'size-4 shrink-0',
									formData.whatsapp_notifications_enabled
										? 'text-emerald-600'
										: 'text-zinc-900',
								)}
								strokeWidth={2.25}
								aria-hidden
							/>
							Envio de notificações no WhatsApp
						</p>
						<p className="mt-0.5 text-xs text-stone-500">
							{formData.whatsapp_notifications_enabled
								? isValidWhatsAppPhone(
										removeMask(formData.phone),
								  )
									? 'O paciente receberá lembretes automáticos.'
									: 'Ativo, mas exige telefone com 11 dígitos para agendar mensagens.'
								: 'Lembretes automáticos desativados.'}{' '}
							<button
								type="button"
								onClick={() => setIsExamplePreviewOpen(true)}
								className="font-semibold text-emerald-700 underline underline-offset-2 transition hover:text-emerald-900"
							>
								ver exemplo
							</button>
						</p>
					</div>
					<button
						type="button"
						role="switch"
						aria-checked={formData.whatsapp_notifications_enabled}
						disabled={!canEditWhatsAppNotifications}
						onClick={() =>
							onChange({
								...formData,
								whatsapp_notifications_enabled:
									!formData.whatsapp_notifications_enabled,
							})
						}
						className={cn(
							'relative inline-flex h-6 w-11 shrink-0 items-center rounded-full border transition-colors',
							formData.whatsapp_notifications_enabled
								? 'border-emerald-600 bg-emerald-600'
								: 'border-stone-300 bg-stone-200',
							!canEditWhatsAppNotifications &&
								'cursor-not-allowed',
						)}
					>
						<span
							className={cn(
								'inline-block size-5 rounded-full bg-white shadow transition-transform',
								formData.whatsapp_notifications_enabled
									? 'translate-x-[22px]'
									: 'translate-x-0.5',
							)}
						/>
					</button>
				</div>
			</section>

			<section className="space-y-4">
				<h3 className={sectionClass}>Responsável legal</h3>
				<div>
					<label className={labelClass}>
						Nome do responsável (opcional)
					</label>
					<input
						type="text"
						value={formData.guardian_full_name}
						onChange={(event) =>
							onChange({
								...formData,
								guardian_full_name: event.target.value,
							})
						}
						className={getFieldClass('guardian_full_name')}
						maxLength={255}
					/>
					<ErrorMsg field="guardian_full_name" />
				</div>
			</section>

			<WhatsAppMessagePreview
				isOpen={isExamplePreviewOpen}
				onClose={() => setIsExamplePreviewOpen(false)}
				message={exampleMessage}
				patientName={formData.full_name.trim() || 'Paciente'}
				patientPhone={removeMask(formData.phone) || null}
			/>
		</div>
	);
};

export { PatientSchedulingFormFields };
